import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { SendHorizonal, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import { type Case, formatDate, formatUSD } from '@/lib/cases'
import { StatusBadge } from '@/components/app/StatusBadge'
import { EmptyState } from '@/components/app/EmptyState'

export function Transmit() {
  const { user } = useAuth()
  const [cases, setCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function fetchCases(userId: string) {
    const { data } = await supabase
      .from('cases')
      .select('*')
      .eq('agent_id', userId)
      .in('status', ['ready', 'review'])
      .order('updated_at', { ascending: false })
    return (data ?? []) as Case[]
  }

  useEffect(() => {
    if (!user) return

    let cancelled = false

    void fetchCases(user.id).then((data) => {
      if (cancelled) return
      setCases(data)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [user])

  async function transmit(c: Case) {
    if (!user) return

    setBusyId(c.id)
    try {
      await supabase.functions.invoke('transmit-to-ace', { body: { case_id: c.id } })
      const refreshedCases = await fetchCases(user.id)
      setCases(refreshedCases)
    } finally {
      setBusyId(null)
    }
  }

  const ready = useMemo(() => cases.filter((c) => c.status === 'ready'), [cases])
  const inReview = useMemo(() => cases.filter((c) => c.status === 'review'), [cases])

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-primary">Transmit</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Send completed entry summaries to ACE. Stubbed in this release — calls return a fake
          ACE receipt.
        </p>
      </header>

      <Section title="Ready to transmit" count={ready.length}>
        {loading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : ready.length === 0 ? (
          <EmptyState
            Icon={SendHorizonal}
            title="Nothing to transmit"
            body="Mark a reviewed case as 'Ready to transmit' from its detail page."
          />
        ) : (
          <ul className="bg-white border border-neutral-200 rounded-lg shadow-sm divide-y divide-neutral-200">
            {ready.map((c) => (
              <li key={c.id} className="grid grid-cols-12 gap-4 items-center p-4">
                <div className="col-span-12 sm:col-span-6 min-w-0">
                  <div className="text-xs font-mono text-neutral-500">{c.case_number}</div>
                  <Link
                    to={`/cases/${c.id}`}
                    className="font-semibold text-primary truncate hover:underline"
                  >
                    {c.title}
                  </Link>
                  <div className="mt-0.5 text-xs text-neutral-500">
                    {c.importer_of_record ?? '—'} &middot; {formatUSD(c.total_value_usd)} entered
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <StatusBadge status={c.status} />
                </div>
                <div className="col-span-6 sm:col-span-3 text-right">
                  <button
                    type="button"
                    onClick={() => void transmit(c)}
                    disabled={busyId === c.id}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded bg-primary text-white font-semibold text-sm hover:bg-primary-dark disabled:opacity-60"
                  >
                    {busyId === c.id ? 'Sending…' : (
                      <>
                        <SendHorizonal size={14} aria-hidden="true" />
                        Transmit (stub)
                      </>
                    )}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="In review" count={inReview.length}>
        {inReview.length === 0 ? (
          <p className="text-sm text-neutral-500">All caught up.</p>
        ) : (
          <ul className="bg-white border border-neutral-200 rounded-lg shadow-sm divide-y divide-neutral-200">
            {inReview.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-4 p-4">
                <Link to={`/cases/${c.id}`} className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-neutral-500">{c.case_number}</div>
                  <div className="font-semibold text-primary truncate">{c.title}</div>
                  <div className="mt-0.5 text-xs text-neutral-500">
                    Last touched {formatDate(c.updated_at)}
                  </div>
                </Link>
                {c.flagged_count > 0 && (
                  <span className="text-xs font-semibold text-accent-coral">
                    {c.flagged_count} flag{c.flagged_count === 1 ? '' : 's'}
                  </span>
                )}
                <CheckCircle2 size={18} className="text-neutral-300" aria-hidden="true" />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </section>
  )
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">{title}</h2>
        <span className="text-xs font-mono text-neutral-400">{count}</span>
      </div>
      {children}
    </div>
  )
}
